// Hooks React Query para el módulo de incidencias/tickets
import { useMemo } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { incidenciaService, clienteIncidenciaService } from '@/services/incidenciaService';
import type {
  IncidenciaCreatePayload,
  IncidenciaFilters,
  IncidenciaMensajeCreatePayload,
  IncidenciaUpdatePayload,
} from '@/types/incidencia';

const STALE_TIME = 30_000; // 30s

// === Query Keys ===

export const incidenciaKeys = {
  all: ['incidencias'] as const,
  lists: () => [...incidenciaKeys.all, 'list'] as const,
  list: (filters: IncidenciaFilters) =>
    [...incidenciaKeys.lists(), JSON.stringify(filters)] as const,
  details: () => [...incidenciaKeys.all, 'detail'] as const,
  detail: (id: string) => [...incidenciaKeys.details(), id] as const,
  messages: (id: string) => [...incidenciaKeys.all, 'messages', id] as const,
  historial: (id: string) => [...incidenciaKeys.all, 'historial', id] as const,
  dashboard: (assignedToId?: string) =>
    [...incidenciaKeys.all, 'dashboard', assignedToId ?? 'all'] as const,
  byExpediente: (expedienteId: string) =>
    [...incidenciaKeys.all, 'expediente', expedienteId] as const,
  clientFlag: (userId: string) =>
    [...incidenciaKeys.all, 'client-flag', userId] as const,
  // Cliente
  clienteList: (estado?: string) =>
    [...incidenciaKeys.all, 'cliente', 'list', estado ?? 'all'] as const,
  clienteDetail: (id: string) =>
    [...incidenciaKeys.all, 'cliente', 'detail', id] as const,
  clienteMessages: (id: string) =>
    [...incidenciaKeys.all, 'cliente', 'messages', id] as const,
};

// === CRM Hooks ===

/** Lista de incidencias con filtros */
export function useIncidencias(filters: IncidenciaFilters = {}) {
  const queryKey = useMemo(() => incidenciaKeys.list(filters), [filters]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => incidenciaService.list(filters),
    staleTime: STALE_TIME,
  });

  return {
    incidencias: data?.items ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error,
    refetch,
  };
}

/** Detalle de una incidencia */
export function useIncidencia(id: string) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: incidenciaKeys.detail(id),
    queryFn: () => incidenciaService.getById(id),
    staleTime: STALE_TIME,
    enabled: !!id,
  });

  return {
    incidencia: data ?? null,
    loading: isLoading,
    error,
    refetch,
  };
}

/** Mensajes de una incidencia */
export function useIncidenciaMessages(id: string) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: incidenciaKeys.messages(id),
    queryFn: () => incidenciaService.getMessages(id),
    staleTime: 10_000,
    enabled: !!id,
    refetchInterval: 15_000, // Poll cada 15s para nuevos mensajes
  });

  return {
    messages: data ?? [],
    loading: isLoading,
    error,
    refetch,
  };
}

/** Historial de una incidencia */
export function useIncidenciaHistorial(id: string) {
  const { data, isLoading } = useQuery({
    queryKey: incidenciaKeys.historial(id),
    queryFn: () => incidenciaService.getHistorial(id),
    staleTime: STALE_TIME,
    enabled: !!id,
  });

  return { historial: data ?? [], loading: isLoading };
}

/** Dashboard stats */
export function useIncidenciaDashboard(assignedToId?: string) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: incidenciaKeys.dashboard(assignedToId),
    queryFn: () => incidenciaService.getDashboardStats(assignedToId),
    staleTime: STALE_TIME,
  });

  return { stats: data ?? null, loading: isLoading, refetch };
}

/** Incidencias por expediente */
export function useIncidenciasByExpediente(expedienteId: string) {
  const { data, isLoading } = useQuery({
    queryKey: incidenciaKeys.byExpediente(expedienteId),
    queryFn: () => incidenciaService.getByExpediente(expedienteId),
    staleTime: STALE_TIME,
    enabled: !!expedienteId,
  });

  return { incidencias: data ?? [], loading: isLoading };
}

/** Flag de cliente */
export function useClientFlag(userId: string) {
  const { data } = useQuery({
    queryKey: incidenciaKeys.clientFlag(userId),
    queryFn: () => incidenciaService.getClientFlag(userId),
    staleTime: 60_000,
    enabled: !!userId,
  });

  return { openCount: data?.open_incidencias_count ?? 0 };
}

// === CRM Mutations ===

export function useCreateIncidencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: IncidenciaCreatePayload) =>
      incidenciaService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.dashboard() });
    },
  });
}

export function useUpdateIncidencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: IncidenciaUpdatePayload;
    }) => incidenciaService.update(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.dashboard() });
    },
  });
}

export function useAddIncidenciaMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: IncidenciaMensajeCreatePayload;
    }) => incidenciaService.addMessage(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.messages(id) });
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.historial(id) });
    },
  });
}

export function useAssignIncidencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      assignedToId,
    }: {
      id: string;
      assignedToId: string;
    }) => incidenciaService.assign(id, assignedToId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.lists() });
    },
  });
}

export function useCloseIncidencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => incidenciaService.close(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: incidenciaKeys.dashboard() });
    },
  });
}

// === Cliente Portal Hooks ===

export function useClienteIncidencias(estado?: string) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: incidenciaKeys.clienteList(estado),
    queryFn: () => clienteIncidenciaService.list(estado),
    staleTime: STALE_TIME,
  });

  return {
    incidencias: data ?? [],
    loading: isLoading,
    error,
    refetch,
  };
}

export function useClienteIncidencia(id: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: incidenciaKeys.clienteDetail(id),
    queryFn: () => clienteIncidenciaService.getById(id),
    staleTime: STALE_TIME,
    enabled: !!id,
  });

  return { incidencia: data ?? null, loading: isLoading, error };
}

export function useClienteIncidenciaMessages(id: string) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: incidenciaKeys.clienteMessages(id),
    queryFn: () => clienteIncidenciaService.getMessages(id),
    staleTime: 10_000,
    enabled: !!id,
    refetchInterval: 15_000,
  });

  return { messages: data ?? [], loading: isLoading, refetch };
}

export function useClienteCreateIncidencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: IncidenciaCreatePayload) =>
      clienteIncidenciaService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: incidenciaKeys.clienteList(),
      });
    },
  });
}

export function useClienteAddMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: IncidenciaMensajeCreatePayload;
    }) => clienteIncidenciaService.addMessage(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: incidenciaKeys.clienteMessages(id),
      });
      queryClient.invalidateQueries({
        queryKey: incidenciaKeys.clienteDetail(id),
      });
    },
  });
}
